import React, { useEffect, useState } from "react";
import { Form, Input, DatePicker, Button, Row, Col, Popconfirm, Flex, Card, Tabs, Select, InputNumber, Typography, Modal, Upload, message } from 'antd';
import Table from 'components/Common/Table';
import { EditOutlined, DeleteOutlined, PlusOutlined, InboxOutlined, EyeOutlined, DownloadOutlined } from "@ant-design/icons"
import * as actions from "store/actions/AuthActions";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from 'NotificationContext';
import DealContactRoleForm from 'components/Deal/DealContactRoleForm';
import DealCompetitorForm from 'components/Deal/DealCompetitorForm';
import DealActivityForm from 'components/Deal/DealActivityForm';
import axios from 'axios';
import fileDownload from 'js-file-download';
import PdfPreviewModal from "components/PdfPreviewModal/PdfPreviewModal";

import dayjs from 'dayjs';

const { Dragger } = Upload;

const { TextArea } = Input;
const { Text } = Typography;

const DealForm = () => {

    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();

    const [contactRoles, setContactRoles] = useState([]);
    const [competitors, setCompetitors] = useState([]);
    const [stageHistory, setStageHistory] = useState([]);
    const [dealActivities, setDealActivities] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [, setLeads] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [dealCategoryId, setDealCategoryId] = useState(null);

    // Form states
    const [contactRoleDrawerVisible, setContactRoleDrawerVisible] = useState(false);
    const [competitorDrawerVisible, setCompetitorDrawerVisible] = useState(false);
    const [activityDrawerVisible, setActivityDrawerVisible] = useState(false);
    const [currentContactRole, setCurrentContactRole] = useState(null);
    const [currentCompetitor, setCurrentCompetitor] = useState(null);
    const [currentActivity, setCurrentActivity] = useState(null);

    // Campaign Quick Create Modal
    const [campaignModalVisible, setCampaignModalVisible] = useState(false);
    const [campaignForm] = Form.useForm();

    // PDF Preview
    const [pdfPreviewData, setPdfPreviewData] = useState(null);
    const [previewPdfVisible, setPreviewPdfVisible] = useState(false);
    const [numPages, setNumPages] = useState(null);

    const notify = useNotification();

    const saveDeal = async (values, saveAndNew = false) => {
        console.log('Received values:', values);
        setLoading(true);

        // Format dates before sending
        const formattedValues = { ...values };
        if (formattedValues.closing_date) {
            formattedValues.closing_date = dayjs(formattedValues.closing_date).format('YYYY-MM-DD');
        }

        // Add category_id if available
        if (dealCategoryId) {
            formattedValues.category_id = dealCategoryId;
        }

        if (id) {
            const response = await actions.updateDeal(formattedValues, id);
            if (response) {
                notify("success", "Deal updated successfully");
                if (saveAndNew) {
                    // Navigate to create new deal
                    navigate('/deals/new');
                    form.resetFields();
                }
            }
        } else {
            const response = await actions.addDeal(formattedValues);
            if (response && response.status === 201) {
                notify("success", "Deal created successfully");
                if (saveAndNew) {
                    // Reset form and stay on create page
                    form.resetFields();
                } else {
                    // Navigate to edit page
                    navigate('/deals/edit/' + response.data['id']);
                }
            }
        }
        setLoading(false);
    };

    const loadDeal = async () => {
        if (id) {
            const response = await actions.retrieveDeal(id);
            if (response) {
                const thisInstance = { ...response }
                let date_fields = ['closing_date']
                for (let field of date_fields) {
                    thisInstance[field] = thisInstance[field] ? dayjs(thisInstance[field]) : null;
                }
                form.setFieldsValue(thisInstance);
                console.log('Contact Roles:', response?.contact_roles);
                console.log('Competitors:', response?.competitors);
                console.log('Deal Activities:', response?.deal_activities);
                console.log('Attachments:', response?.attachments);
                setContactRoles(response?.contact_roles || []);
                setCompetitors(response?.competitors || []);
                setDealActivities(response?.deal_activities || []);
                setAttachments(response?.attachments || []);

                // Load stage history
                try {
                    const historyData = await actions.loadDealStageHistory(id);
                    console.log('Stage History:', historyData);
                    if (historyData && Array.isArray(historyData)) {
                        setStageHistory(historyData);
                    }
                } catch (error) {
                    console.error('Failed to load stage history:', error);
                    // Don't show error to user, just log it
                }
            }
        }
        setLoading(false);
    }

    const loadEmployees = async () => {
        const response = await actions.loadEmployees();
        console.log('Employees response:', response);
        if (response && response.results) {
            console.log('Employees data:', response.results);
            setEmployees(response.results);
        }
    }

    const loadAccounts = async () => {
        const response = await actions.loadAccounts(1, '');
        if (response && response.status === 200) {
            setAccounts(response.data.results || []);
        }
    }

    const loadContacts = async () => {
        const response = await actions.loadContacts(1, '');
        if (response && response.status === 200) {
            setContacts(response.data.results || []);
        }
    }

    const loadLeads = async () => {
        const response = await actions.loadLeads(1, '');
        if (response && response.status === 200) {
            setLeads(response.data.results || []);
        }
    }

    const loadCampaigns = async () => {
        const response = await actions.loadCampaigns(1, '');
        if (response && response.status === 200) {
            setCampaigns(response.data.results || []);
        }
    }

    const handleQuickCreateCampaign = async (values) => {
        try {
            // Format dates before sending
            const formattedValues = { ...values };
            if (formattedValues.start_date) {
                formattedValues.start_date = dayjs(formattedValues.start_date).format('YYYY-MM-DD');
            }
            if (formattedValues.end_date) {
                formattedValues.end_date = dayjs(formattedValues.end_date).format('YYYY-MM-DD');
            }

            const response = await actions.addCampaign(formattedValues);
            if (response && response.status === 201) {
                notify("success", "Campaign created successfully");
                // Add new campaign to the list
                setCampaigns([...campaigns, response.data]);
                // Set the new campaign as selected
                form.setFieldsValue({ campaign_source: response.data.campaign_name });
                // Close modal and reset form
                setCampaignModalVisible(false);
                campaignForm.resetFields();
            }
        } catch (error) {
            notify("error", "Failed to create campaign");
        }
    }

    useEffect(() => {
        loadEmployees();
        loadAccounts();
        loadContacts();
        loadLeads();
        loadCampaigns();

        // Fetch DEALS category
        const fetchDealCategory = async () => {
            const categoryId = await actions.getDocumentCategoryByCode('DEALS');
            setDealCategoryId(categoryId);
        };
        fetchDealCategory();

        if (id) {
            loadDeal()
        }
    }, [id]);

    const handleDeleteContactRole = async (roleId) => {
        const response = await actions.deleteDealContactRole(roleId);
        if (response) {
            setContactRoles(contactRoles.filter(item => item.id !== roleId));
            notify("success", "Contact role removed successfully");
        }
    }

    const handleDeleteCompetitor = async (competitorId) => {
        const response = await actions.deleteDealCompetitor(competitorId);
        if (response) {
            setCompetitors(competitors.filter(item => item.id !== competitorId));
            notify("success", "Competitor removed successfully");
        }
    }

    const handleDeleteActivity = async (activityId) => {
        const response = await actions.deleteDealActivity(activityId);
        if (response) {
            setDealActivities(dealActivities.filter(item => item.id !== activityId));
            notify("success", "Activity removed successfully");
        }
    }

    const handleDeleteAttachment = async (doc_id) => {
        const response = await actions.removeDocumentFromDeals(id, doc_id);
        if (response) {
            setAttachments(response);
            notify("success", "Attachment removed successfully");
        }
    }

    // Upload props for drag and drop
    const props = {
        name: 'file',
        multiple: false,
        action: process.env.REACT_APP_BASE_URL + `/deal/deals/${id}/addAttachment/`,
        headers: { "Authorization": axios.defaults.headers.common['Authorization'] },
        data: { category_id: dealCategoryId },
        onChange(info) {
            console.log(info);
            const { status, response } = info.file;
            if (status !== 'uploading') {
                console.log(info.file, info.fileList);
            }
            if (status === 'done') {
                message.success(`${info.file.name} file uploaded successfully.`);
            } else if (status === 'error') {
                message.error(`${info.file.name} file upload failed.`);
            }

            if (response) {
                setAttachments(response);
            }
        },
        onDrop(e) {
            console.log('Dropped files', e.dataTransfer.files);
        },
    };

    const viewDownloadDocument = async (record, preview, download) => {
        try {
            const response = await axios.get(record.document['document_file'], {
                responseType: "blob",
            });
            if (response.status === 200) {
                if (download) {
                    fileDownload(response.data, record.document['name']);
                }
                if (preview) {
                    if (record.document['extension'] === ".pdf") {
                        setPdfPreviewData(response.data);
                        setPreviewPdfVisible(true);
                    } else {
                        notify("error", "only pdfs can be rendered for now");
                    }
                }
            }
        } catch (err) {
            console.log("Error: ", err);
        }
    }

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    // Calculate expected revenue
    const amount = form.getFieldValue('amount') || 0;
    const probability = form.getFieldValue('probability') || 0;
    const expectedRevenue = (amount * probability / 100).toFixed(2);

    const contactRoleColumns = [
        {
            title: 'Contact Name',
            dataIndex: 'contact_name',
            key: 'contact_name',
        },
        {
            title: 'Role',
            dataIndex: 'role_name',
            key: 'role_name',
        },
        {
            title: 'Primary',
            dataIndex: 'is_primary',
            key: 'is_primary',
            render: (val) => val ? 'Yes' : 'No',
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Actions',
            dataIndex: 'action',
            render: (_, record) => {
                return (
                    <Flex gap="middle">
                        <a onClick={() => {
                            setCurrentContactRole(record);
                            setContactRoleDrawerVisible(true);
                        }}>
                            <EditOutlined />
                        </a>
                        <Popconfirm title="Sure to remove?" onConfirm={() => handleDeleteContactRole(record.id)}>
                            <DeleteOutlined />
                        </Popconfirm>
                    </Flex>
                );
            },
        },
    ];

    const competitorColumns = [
        {
            title: 'Competitor Name',
            dataIndex: 'competitor_name',
            key: 'competitor_name',
        },
        {
            title: 'Website',
            dataIndex: 'website',
            key: 'website',
        },
        {
            title: 'Strengths',
            dataIndex: 'strengths',
            key: 'strengths',
        },
        {
            title: 'Weaknesses',
            dataIndex: 'weaknesses',
            key: 'weaknesses',
        },
        {
            title: 'Actions',
            dataIndex: 'action',
            render: (_, record) => {
                return (
                    <Flex gap="middle">
                        <a onClick={() => {
                            setCurrentCompetitor(record);
                            setCompetitorDrawerVisible(true);
                        }}>
                            <EditOutlined />
                        </a>
                        <Popconfirm title="Sure to remove?" onConfirm={() => handleDeleteCompetitor(record.id)}>
                            <DeleteOutlined />
                        </Popconfirm>
                    </Flex>
                );
            },
        },
    ];

    const tabElements = [
        {
            label: `Overview`,
            key: '1',
            children:
                <div>
                    {id && (
                        <Row gutter={16} style={{ marginBottom: 24 }}>
                            <Col span={8}>
                                <Card>
                                    <Text type="secondary">Expected Revenue</Text>
                                    <div style={{ fontSize: 24, fontWeight: 'bold' }}>Rs. {expectedRevenue}</div>
                                    <Text type="secondary" style={{ fontSize: 11 }}>Amount × Probability</Text>
                                </Card>
                            </Col>
                            <Col span={8}>
                                <Card>
                                    <Text type="secondary">Deal Stage</Text>
                                    <div style={{ fontSize: 24, fontWeight: 'bold' }}>{form.getFieldValue('stage_display') || '-'}</div>
                                </Card>
                            </Col>
                            <Col span={8}>
                                <Card>
                                    <Text type="secondary">Probability</Text>
                                    <div style={{ fontSize: 24, fontWeight: 'bold' }}>{probability}%</div>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </div>,
        },
        {
            label: `Contact Roles`,
            key: '2',
            children:
                <div>
                    <Flex justify="end">
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setCurrentContactRole(null);
                                setContactRoleDrawerVisible(true);
                            }}
                            style={{ marginBottom: 16 }}
                        >
                            Add Contact Role
                        </Button>
                    </Flex>
                    <Table
                        dataSource={contactRoles}
                        columns={contactRoleColumns}
                        rowKey="id"
                        rowClassName="editable-row"
                        size="small"
                        pagination={false}
                    />
                </div>,
        },
        {
            label: `Stage History`,
            key: '3',
            children:
                <div>
                    <Table
                        dataSource={stageHistory}
                        rowKey="history_id"
                        columns={[
                            {
                                title: 'Date',
                                dataIndex: 'history_date',
                                key: 'history_date',
                                render: (text) => dayjs(text).format('DD/MM/YYYY HH:mm'),
                            },
                            {
                                title: 'Stage',
                                dataIndex: 'stage_display',
                                key: 'stage_display',
                            },
                            {
                                title: 'Amount',
                                dataIndex: 'amount',
                                key: 'amount',
                                render: (text) => `Rs. ${parseFloat(text).toLocaleString()}`,
                            },
                            {
                                title: 'Probability',
                                dataIndex: 'probability',
                                key: 'probability',
                                render: (text) => `${text}%`,
                            },
                            {
                                title: 'Expected Revenue',
                                dataIndex: 'expected_revenue',
                                key: 'expected_revenue',
                                render: (text) => `Rs. ${parseFloat(text).toLocaleString()}`,
                            },
                            {
                                title: 'Closing Date',
                                dataIndex: 'closing_date',
                                key: 'closing_date',
                                render: (text) => dayjs(text).format('DD/MM/YYYY'),
                            },
                            {
                                title: 'Modified By',
                                dataIndex: 'history_user',
                                key: 'history_user',
                            },
                        ]}
                        rowClassName="editable-row"
                        size="small"
                        pagination={false}
                    />
                </div>,
        },
        {
            label: `Competitors`,
            key: '4',
            children:
                <div>
                    <Flex justify="end">
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setCurrentCompetitor(null);
                                setCompetitorDrawerVisible(true);
                            }}
                            style={{ marginBottom: 16 }}
                        >
                            Add Competitor
                        </Button>
                    </Flex>
                    <Table
                        dataSource={competitors}
                        columns={competitorColumns}
                        rowKey="id"
                        rowClassName="editable-row"
                        size="small"
                        pagination={false}
                    />
                </div>,
        },
        {
            label: `Activities`,
            key: '5',
            children:
                <div>
                    <Flex justify="end">
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setCurrentActivity(null);
                                setActivityDrawerVisible(true);
                            }}
                            style={{ marginBottom: 16 }}
                        >
                            Add Activity
                        </Button>
                    </Flex>
                    <Table
                        dataSource={dealActivities}
                        columns={[
                            {
                                title: 'Activity Type',
                                dataIndex: 'activity_type_display',
                                key: 'activity_type_display',
                            },
                            {
                                title: 'Date',
                                dataIndex: 'activity_date',
                                key: 'activity_date',
                                render: (text) => text ? dayjs(text).format('DD/MM/YYYY HH:mm') : '-',
                            },
                            {
                                title: 'Status',
                                dataIndex: 'status_display',
                                key: 'status_display',
                            },
                            {
                                title: 'Notes',
                                dataIndex: 'notes',
                                key: 'notes',
                                ellipsis: true,
                            },
                            {
                                title: 'Actions',
                                dataIndex: 'action',
                                render: (_, record) => {
                                    return (
                                        <Flex gap="middle">
                                            <a onClick={() => {
                                                setCurrentActivity(record);
                                                setActivityDrawerVisible(true);
                                            }}>
                                                <EditOutlined />
                                            </a>
                                            <Popconfirm title="Sure to remove?" onConfirm={() => handleDeleteActivity(record.id)}>
                                                <DeleteOutlined />
                                            </Popconfirm>
                                        </Flex>
                                    );
                                },
                            },
                        ]}
                        rowKey="id"
                        rowClassName="editable-row"
                        size="small"
                        pagination={false}
                    />
                </div>,
        },
        {
            label: `Attachments`,
            key: '6',
            children:
                <div>
                    <Dragger {...props}>
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">Click or drag file to this area to upload</p>
                        <p className="ant-upload-hint">
                            Support for a single or bulk upload. Strictly prohibit from uploading company data or other
                            band files
                        </p>
                    </Dragger>
                    <Table
                        dataSource={attachments}
                        columns={[
                            {
                                title: 'Name',
                                dataIndex: ["document", "name"],
                                key: 'name',
                                sorter: (a, b) => a?.document?.name?.localeCompare(b?.document?.name),
                            },
                            {
                                title: 'Date',
                                dataIndex: 'created_at',
                                key: 'created_at',
                                sorter: (a, b) => dayjs(a?.created_at) - dayjs(b?.created_at),
                                render: (text, record) => {
                                    return <div>{dayjs(text).format('MMMM Do YYYY, h:mm:ss a')}</div>
                                }
                            },
                            {
                                title: 'Size',
                                dataIndex: ["document", "size"],
                                key: 'size',
                                sorter: (a, b) => (a?.document?.org_size || 0) - (b?.document?.org_size || 0),
                            },
                            {
                                title: 'Linked',
                                dataIndex: ["document", "is_linked"],
                                key: 'is_linked',
                                sorter: (a, b) => (a?.document?.is_linked ? 1 : 0) - (b?.document?.is_linked ? 1 : 0),
                                render: (_, record) => {
                                    const size = 8;
                                    const radius = "30%";
                                    return (
                                        <div className='d-flex justify-content-center align-items-center'>
                                            {record?.document?.is_linked ? <>
                                                <div style={{ width: size, height: size, backgroundColor: "green", borderRadius: radius }}>
                                                </div>
                                            </> : <>
                                                <div style={{ width: size, height: size, backgroundColor: "red", borderRadius: radius }}>
                                                </div>
                                            </>}
                                        </div>
                                    )
                                }
                            },
                            {
                                title: 'Actions',
                                dataIndex: 'action',
                                render: (_, record) => {
                                    return (
                                        <Flex gap="middle">
                                            <Typography.Link onClick={() => { console.log("View Document"); viewDownloadDocument(record, true, false); }}>
                                                <EyeOutlined />
                                            </Typography.Link>
                                            <Typography.Link onClick={() => { console.log("Download Document"); viewDownloadDocument(record, false, true); }}>
                                                <DownloadOutlined />
                                            </Typography.Link>
                                            <Popconfirm title="Sure to delete?" onConfirm={() => handleDeleteAttachment(record.id)}>
                                                <DeleteOutlined />
                                            </Popconfirm>
                                        </Flex>
                                    );
                                },
                            },
                        ]}
                        rowKey="id"
                        rowClassName="editable-row"
                        size="small"
                        pagination={false}
                    />
                </div>,
        },
    ]

    return (
        <>
            <div className='custom-scroll-div' style={{ overflowY: "scroll", maxHeight: "90vh", padding: 10, paddingBottom: 30 }}>
                <Form form={form} onFinish={saveDeal} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} labelAlign="left">
                    <Flex justify='space-between' align='center' style={{ margin: "10px auto" }}>
                        <Typography.Title level={3}>{id ? 'Edit' : 'Create'} Deal</Typography.Title>
                        <Flex gap='middle'>
                            <Button onClick={() => navigate('/deals')}>Cancel</Button>
                            <Button
                                type="default"
                                onClick={() => form.validateFields().then(values => saveDeal(values, true))}
                                loading={loading}
                            >
                                Save and New
                            </Button>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                Save
                            </Button>
                        </Flex>
                    </Flex>

                    {/* Deal Overview - Displayed at the top */}
                    {id && (
                        <Card style={{ marginBottom: 16, backgroundColor: '#fafafa' }} bodyStyle={{ padding: '16px 24px' }}>
                            <Row gutter={[32, 16]}>
                                <Col span={6}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Deal Owner</Text>
                                    <div style={{ fontSize: 14, marginTop: 4 }}>
                                        {(() => {
                                            const employee = employees.find(e => e.id === form.getFieldValue('employee_owner'));
                                            if (employee) {
                                                const userName = employee.user_name || '';
                                                const cleanName = userName.split(' ')
                                                    .filter(part => part && part !== 'None')
                                                    .join(' ')
                                                    .trim();
                                                return cleanName || employee.employee_id || '-';
                                            }
                                            return '-';
                                        })()}
                                    </div>
                                </Col>
                                <Col span={6}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Stage</Text>
                                    <div style={{ fontSize: 14, marginTop: 4 }}>
                                        {form.getFieldValue('stage') ? form.getFieldValue('stage').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '-'}
                                    </div>
                                </Col>
                                <Col span={4}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Probability (%)</Text>
                                    <div style={{ fontSize: 14, marginTop: 4 }}>{form.getFieldValue('probability') || 0}</div>
                                </Col>
                                <Col span={4}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Expected Revenue</Text>
                                    <div style={{ fontSize: 14, marginTop: 4 }}>
                                        Rs. {parseFloat(form.getFieldValue('expected_revenue') || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                    </div>
                                </Col>
                                <Col span={4}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Closing Date</Text>
                                    <div style={{ fontSize: 14, marginTop: 4 }}>
                                        {form.getFieldValue('closing_date') ? dayjs(form.getFieldValue('closing_date')).format('DD/MM/YYYY') : '-'}
                                    </div>
                                </Col>
                            </Row>
                        </Card>
                    )}

                    <Row>
                        <Col span={12}>
                            <Card title="Deal Information" bordered={false}>
                                {id && (
                                    <Form.Item label="Deal Number" name="deal_number">
                                        <Input disabled style={{ fontWeight: 'bold', color: '#1890ff' }} />
                                    </Form.Item>
                                )}
                                <Form.Item label="Deal Owner" name="employee_owner">
                                    <Select
                                        showSearch
                                        placeholder="Select deal owner"
                                        optionFilterProp="children"
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={employees.map(employee => {
                                            // Clean up user_name by removing "None" and extra spaces
                                            const userName = employee.user_name || '';
                                            const cleanName = userName.split(' ')
                                                .filter(part => part && part !== 'None')
                                                .join(' ')
                                                .trim();

                                            return {
                                                value: employee.id,
                                                label: `${cleanName || 'Unknown'} (${employee.employee_id || 'N/A'})`
                                            };
                                        })}
                                    />
                                </Form.Item>
                                <Form.Item label="Deal Name" name="deal_name" rules={[{ required: true, message: 'Please input deal name!' }]}>
                                    <Input />
                                </Form.Item>
                                <Form.Item label="Account" name="account" rules={[{ required: true, message: 'Please select account!' }]}>
                                    <Select
                                        showSearch
                                        placeholder="Select account"
                                        optionFilterProp="children"
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={accounts.map(account => ({
                                            value: account.id,
                                            label: account.name
                                        }))}
                                    />
                                </Form.Item>
                                <Form.Item label="Contact" name="contact">
                                    <Select
                                        showSearch
                                        placeholder="Select contact"
                                        optionFilterProp="children"
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={contacts.map(contact => ({
                                            value: contact.id,
                                            label: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unnamed Contact'
                                        }))}
                                    />
                                </Form.Item>
                                <Form.Item label="Amount" name="amount" rules={[{ required: true, message: 'Please input amount!' }]}>
                                    <InputNumber
                                        prefix="Rs."
                                        style={{ width: '100%' }}
                                        min={0}
                                        precision={2}
                                    />
                                </Form.Item>
                                <Form.Item label="Closing Date" name="closing_date" rules={[{ required: true, message: 'Please select closing date!' }]}>
                                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                                </Form.Item>
                            </Card>
                        </Col>
                        <Col span={12} style={{ padding: "0px 0px 10px 10px" }}>
                            <Card title="Deal Details" bordered={false}>
                                <Form.Item label="Stage" name="stage" rules={[{ required: true, message: 'Please select stage!' }]}>
                                    <Select>
                                        <Select.Option value="QUALIFICATION">Qualification</Select.Option>
                                        <Select.Option value="NEEDS_ANALYSIS">Needs Analysis</Select.Option>
                                        <Select.Option value="VALUE_PROPOSITION">Value Proposition</Select.Option>
                                        <Select.Option value="IDENTIFY_DECISION_MAKERS">Identify Decision Makers</Select.Option>
                                        <Select.Option value="PROPOSAL_PRICE_QUOTE">Proposal/Price Quote</Select.Option>
                                        <Select.Option value="NEGOTIATION_REVIEW">Negotiation/Review</Select.Option>
                                        <Select.Option value="CLOSED_WON">Closed Won</Select.Option>
                                        <Select.Option value="CLOSED_LOST">Closed Lost</Select.Option>
                                        <Select.Option value="CLOSED_LOST_TO_COMPETITION">Closed Lost to Competition</Select.Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Probability (%)" name="probability">
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        min={0}
                                        max={100}
                                    />
                                </Form.Item>
                                <Form.Item label="Deal Type" name="deal_type">
                                    <Select>
                                        <Select.Option value="NEW_BUSINESS">New Business</Select.Option>
                                        <Select.Option value="EXISTING_BUSINESS">Existing Business</Select.Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Lead Source" name="lead_source">
                                    <Select placeholder="Select lead source">
                                        <Select.Option value="NONE">-None-</Select.Option>
                                        <Select.Option value="ADVERTISEMENT">Advertisement</Select.Option>
                                        <Select.Option value="COLD_CALL">Cold Call</Select.Option>
                                        <Select.Option value="EMPLOYEE_REFERRAL">Employee Referral</Select.Option>
                                        <Select.Option value="EXTERNAL_REFERRAL">External Referral</Select.Option>
                                        <Select.Option value="ONLINE_STORE">Online Store</Select.Option>
                                        <Select.Option value="PARTNER">Partner</Select.Option>
                                        <Select.Option value="PUBLIC_RELATIONS">Public Relations</Select.Option>
                                        <Select.Option value="SALES_EMAIL_ALIAS">Sales Email Alias</Select.Option>
                                        <Select.Option value="SEMINAR_PARTNER">Seminar Partner</Select.Option>
                                        <Select.Option value="INTERNAL_SEMINAR">Internal Seminar</Select.Option>
                                        <Select.Option value="TRADE_SHOW">Trade Show</Select.Option>
                                        <Select.Option value="WEB_DOWNLOAD">Web Download</Select.Option>
                                        <Select.Option value="WEB_RESEARCH">Web Research</Select.Option>
                                        <Select.Option value="CHAT">Chat</Select.Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Campaign Source" name="campaign_source">
                                    <Select
                                        showSearch
                                        placeholder="Select campaign"
                                        optionFilterProp="children"
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        onChange={(value) => {
                                            if (value === '__quick_create__') {
                                                setCampaignModalVisible(true);
                                                form.setFieldsValue({ campaign_source: null });
                                            }
                                        }}
                                        dropdownRender={(menu) => (
                                            <>
                                                {menu}
                                                <div style={{ borderTop: '1px solid #f0f0f0', padding: '8px 12px', cursor: 'pointer' }}
                                                    onClick={() => {
                                                        setCampaignModalVisible(true);
                                                        form.setFieldsValue({ campaign_source: null });
                                                    }}
                                                >
                                                    <span style={{ color: '#1890ff', fontWeight: 500 }}>Quick Create Campaign</span>
                                                </div>
                                            </>
                                        )}
                                        options={[
                                            { value: null, label: '-None-' },
                                            ...campaigns.map(campaign => ({
                                                value: campaign.campaign_name,
                                                label: campaign.campaign_name
                                            }))
                                        ]}
                                    />
                                </Form.Item>
                            </Card>
                        </Col>
                    </Row>
                    <Row style={{ padding: "10px 0px 0px 0px" }}>
                        <Col span={12}>
                            <Card title="Additional Information" bordered={false}>
                                <Form.Item label="Next Step" name="next_step" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                                    <Input />
                                </Form.Item>
                                <Form.Item label="Loss Reason" name="loss_reason" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                                    <Input />
                                </Form.Item>
                            </Card>
                        </Col>
                        <Col span={12} style={{ padding: "0px 0px 0px 10px" }}>
                            <Card title="Description" bordered={false}>
                                <Form.Item label="Description" name="description" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                                    <TextArea rows={4} />
                                </Form.Item>
                            </Card>
                        </Col>
                    </Row>
                    {id && <Row style={{ padding: "10px 0px 0px 0px" }}>
                        <Col span={24}>
                            <div className="site-card-border-less-wrapper">
                                <Card bordered={false}>
                                    <Tabs
                                        defaultActiveKey="1"
                                        items={tabElements}
                                    />
                                </Card>
                            </div>
                        </Col>
                    </Row>}
                </Form>
            </div>

            {/* Contact Role Drawer */}
            <DealContactRoleForm
                visible={contactRoleDrawerVisible}
                onClose={() => {
                    setContactRoleDrawerVisible(false);
                    setCurrentContactRole(null);
                }}
                dealId={id}
                instance={currentContactRole}
                contacts={contacts}
                onSuccess={() => {
                    loadDeal();
                    setContactRoleDrawerVisible(false);
                    setCurrentContactRole(null);
                }}
            />

            {/* Competitor Drawer */}
            <DealCompetitorForm
                visible={competitorDrawerVisible}
                onClose={() => {
                    setCompetitorDrawerVisible(false);
                    setCurrentCompetitor(null);
                }}
                dealId={id}
                instance={currentCompetitor}
                onSuccess={() => {
                    loadDeal();
                    setCompetitorDrawerVisible(false);
                    setCurrentCompetitor(null);
                }}
            />

            {/* Activity Drawer */}
            <DealActivityForm
                visible={activityDrawerVisible}
                onClose={() => {
                    setActivityDrawerVisible(false);
                    setCurrentActivity(null);
                }}
                dealId={id}
                instance={currentActivity}
                onSuccess={() => {
                    loadDeal();
                    setActivityDrawerVisible(false);
                    setCurrentActivity(null);
                }}
            />

            {/* PDF Preview Modal */}
            <PdfPreviewModal
                numPages={numPages}
                onDocumentLoadSuccess={onDocumentLoadSuccess}
                pdfPreviewData={pdfPreviewData}
                previewPdfVisible={previewPdfVisible}
                setPreviewPdfVisible={setPreviewPdfVisible}
            />

            {/* Campaign Quick Create Modal */}
            <Modal
                title="Quick Create: Campaign"
                open={campaignModalVisible}
                onCancel={() => {
                    setCampaignModalVisible(false);
                    campaignForm.resetFields();
                }}
                onOk={() => campaignForm.submit()}
                okText="Create"
                cancelText="Cancel"
                width={600}
            >
                <Form
                    form={campaignForm}
                    onFinish={handleQuickCreateCampaign}
                    layout="vertical"
                >
                    <Form.Item
                        label="Campaign Name"
                        name="campaign_name"
                        rules={[{ required: true, message: 'Please input campaign name!' }]}
                    >
                        <Input placeholder="Enter campaign name" />
                    </Form.Item>

                    <Form.Item label="Type" name="type">
                        <Select placeholder="Select type">
                            <Select.Option value="NONE">-None-</Select.Option>
                            <Select.Option value="CONFERENCE">Conference</Select.Option>
                            <Select.Option value="WEBINAR">Webinar</Select.Option>
                            <Select.Option value="TRADE_SHOW">Trade Show</Select.Option>
                            <Select.Option value="PUBLIC_RELATIONS">Public Relations</Select.Option>
                            <Select.Option value="PARTNERS">Partners</Select.Option>
                            <Select.Option value="REFERRAL_PROGRAM">Referral Program</Select.Option>
                            <Select.Option value="ADVERTISEMENT">Advertisement</Select.Option>
                            <Select.Option value="BANNER_ADS">Banner Ads</Select.Option>
                            <Select.Option value="DIRECT_MAIL">Direct Mail</Select.Option>
                            <Select.Option value="EMAIL">Email</Select.Option>
                            <Select.Option value="TELEMARKETING">Telemarketing</Select.Option>
                            <Select.Option value="OTHERS">Others</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item label="Status" name="status">
                        <Select placeholder="Select status">
                            <Select.Option value="NONE">-None-</Select.Option>
                            <Select.Option value="PLANNING">Planning</Select.Option>
                            <Select.Option value="ACTIVE">Active</Select.Option>
                            <Select.Option value="INACTIVE">Inactive</Select.Option>
                            <Select.Option value="COMPLETE">Complete</Select.Option>
                        </Select>
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Start Date" name="start_date">
                                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="End Date" name="end_date">
                                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item label="Expected Revenue" name="expected_revenue">
                        <InputNumber
                            prefix="Rs."
                            style={{ width: '100%' }}
                            min={0}
                            precision={2}
                            placeholder="Enter expected revenue"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}

export default DealForm;
